import {
  formatDate,
  formatIntegerOrDash,
  formatRelativeTime,
  formatRupees,
} from '@utils/format';
import type { MarketPrice, MarketPriceDataSource } from '../schema';

interface Props {
  row: MarketPrice;
  onClick: () => void;
}

const SOURCE_LABELS: Record<MarketPriceDataSource, string> = {
  morning_placeholder: 'Placeholder',
  field: 'Field',
  scrape: 'Scraped',
  field_correction: 'Corrected',
};

const SOURCE_TONES: Record<MarketPriceDataSource, string> = {
  morning_placeholder: 'bg-warning/[0.12] text-warning',
  field: 'bg-primary/[0.12] text-primary',
  scrape: 'bg-foreground-muted/[0.14] text-foreground-muted',
  field_correction: 'bg-success/[0.14] text-success',
};

/**
 * Mobile card view for a single market-price row. Rendered by
 * `<ResponsiveDataList>` below md (768px). Tap → opens the edit dialog.
 *
 * Layout:
 *   Crop · Variety              [source pill]
 *   Date · APMC name
 *   ──────────────────
 *   MIN     MODAL     MAX
 *   ₹50     ₹55       ₹60   /quintal
 *   ──────────────────
 *   Arrivals: 200 q · Updated 2h ago
 */
export function MarketPriceCard({ row, onClick }: Props) {
  const cropLabel = row.varietyName
    ? `${row.cropName} · ${row.varietyName}`
    : row.cropName;

  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative flex flex-col gap-3 rounded-xl border border-border bg-surface px-4 py-3 text-left transition-colors hover:border-primary/40 hover:bg-primary/[0.02] active:bg-primary/[0.04]"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate text-[14.5px] font-semibold text-foreground">
            {cropLabel}
          </div>
          <div className="mt-0.5 truncate text-[12.5px] text-foreground-muted">
            {formatDate(row.priceDate)} · {row.apmcName}
          </div>
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${SOURCE_TONES[row.dataSource]}`}
        >
          {SOURCE_LABELS[row.dataSource]}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2 rounded-lg bg-surface-muted px-3 py-2">
        <PriceCell label="Min" value={formatRupees(row.minPrice)} />
        <PriceCell
          label="Modal"
          value={formatRupees(row.modalPrice)}
          emphasis
        />
        <PriceCell label="Max" value={formatRupees(row.maxPrice)} />
      </div>

      <div className="flex items-center justify-between text-[11.5px] text-foreground-muted">
        <span>
          {row.commodityArrivals != null
            ? `Arrivals: ${formatIntegerOrDash(row.commodityArrivals)} ${row.unit}`
            : `Arrivals: —`}
        </span>
        <span>Updated {formatRelativeTime(row.updatedAt)}</span>
      </div>
    </button>
  );
}

function PriceCell({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div className="flex min-w-0 flex-col leading-tight">
      <span className="text-[10px] font-medium uppercase tracking-wider text-foreground-muted">
        {label}
      </span>
      <span
        className={
          emphasis
            ? 'truncate text-[15px] font-semibold tabular-nums text-foreground'
            : 'truncate text-[14px] tabular-nums text-foreground'
        }
      >
        {value}
      </span>
    </div>
  );
}
